import React, { useEffect, useMemo, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

/**
 * ModalCheckboxPicker (Material UI, JavaScript)
 *
 * Props:
 *  - items: Array<{ id: string|number, label: string, description?: string }>
 *  - title?: string
 *  - confirmText?: string (default: "Done")
 *  - cancelText?: string (default: "Cancel")
 *  - onConfirm?: (selectedIds: Array<string|number>) => void
 *  - initialSelected?: Array<string|number | {id: string|number}>
 *  - onCreate?: (id: string|number) => Promise<void>      // ← NEW
 *  - onDelete?: (id: string|number) => Promise<void>      // ← NEW
 */
export function ModalCheckboxPicker({
  items,
  title = "Select items",
  confirmText = "Done",
  cancelText = "Cancel",
  onConfirm,
  initialSelected = [],
  onCreate,                // ← NEW
  onDelete,                // ← NEW
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set(initialSelected));
  const userActionRef = useRef(false); // track true only on user toggle, not on initial set

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      [it.label, it.description].filter(Boolean).some((t) => String(t).toLowerCase().includes(q))
    );
  }, [items, query]);

  useEffect(() => {
    if (Array.isArray(initialSelected)) {
      const ids = initialSelected.map((v) => (typeof v === "object" ? v.id : v));
      setSelected(new Set(ids));
    }
  }, [initialSelected]);

  // optimistic toggle with rollback on error
  const toggle = async (id) => {
    userActionRef.current = true;

    const wasSelected = selected.has(id);
    // optimistic update
    setSelected((prev) => {
      const next = new Set(prev);
      wasSelected ? next.delete(id) : next.add(id);
      return next;
    });

    try {
      if (wasSelected) {
        // UNSELECT → DELETE
        if (onDelete) await onDelete(id);
      } else {
        // SELECT → CREATE
        if (onCreate) await onCreate(id);
      }
    } catch (err) {
      console.error("Toggle failed, reverting", err);
      // rollback
      setSelected((prev) => {
        const next = new Set(prev);
        // revert the optimistic change
        wasSelected ? next.add(id) : next.delete(id);
        return next;
      });
    } finally {
      userActionRef.current = false;
    }
  };

  const selectedArray = useMemo(() => Array.from(selected), [selected]);

  const allFilteredIds = useMemo(() => filtered.map((i) => i.id), [filtered]);
  const allShownSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.has(id));

  const handleSelectShown = async () => {
    // select all currently filtered (only create those not yet selected)
    const toCreate = allFilteredIds.filter((id) => !selected.has(id));
    if (toCreate.length === 0) return;

    // optimistic
    setSelected((prev) => new Set([...prev, ...toCreate]));
    try {
      if (onCreate) {
        // run sequentially to keep it simple; batch if you prefer
        for (const id of toCreate) await onCreate(id);
      }
    } catch (err) {
      console.error("Select shown failed, reverting", err);
      // rollback only the ones we tried to add
      setSelected((prev) => {
        const next = new Set(prev);
        toCreate.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const handleUnselectShown = async () => {
    const toDelete = allFilteredIds.filter((id) => selected.has(id));
    if (toDelete.length === 0) return;

    // optimistic
    setSelected((prev) => {
      const next = new Set(prev);
      toDelete.forEach((id) => next.delete(id));
      return next;
    });
    try {
      if (onDelete) {
        for (const id of toDelete) await onDelete(id);
      }
    } catch (err) {
      console.error("Unselect shown failed, reverting", err);
      // rollback
      setSelected((prev) => new Set([...prev, ...toDelete]));
    }
  };

  const handleClearAll = async () => {
    const toDelete = Array.from(selected);
    if (toDelete.length === 0) return;

    // optimistic
    setSelected(new Set());
    try {
      if (onDelete) {
        for (const id of toDelete) await onDelete(id);
      }
    } catch (err) {
      console.error("Clear all failed, reverting", err);
      // rollback
      setSelected(new Set(toDelete));
    }
  };

  const handleConfirm = () => {
    onConfirm && onConfirm(selectedArray);
    setOpen(false);
  };

  return (
    <>
      <div className="plus-cont" onClick={() => setOpen(true)}>
        <span className="plus">+</span>
        <span className="tooltiptext">Թույլ տալ ագենդին թասկ դնել</span>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Search"
              placeholder="Type to filter…"
              variant="outlined"
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              fullWidth
            />

            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={allShownSelected ? handleUnselectShown : handleSelectShown}>
                {allShownSelected ? "Unselect shown" : "Select shown"}
              </Button>
              {selectedArray.length > 0 && (
                <Button size="small" variant="text" onClick={handleClearAll}>Clear all</Button>
              )}
            </Stack>

            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, height: 320, overflow: "auto" }}>
              <List disablePadding>
                {filtered.map((it) => {
                  const checked = selected.has(it.id);
                  return (
                    <ListItem key={it.id} disablePadding>
                      <ListItemButton role={undefined} onClick={() => toggle(it.id)} dense>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={checked}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{ "aria-labelledby": `lbl-${it.id}` }}
                          />
                        </ListItemIcon>
                        <ListItemText id={`lbl-${it.id}`} primary={it.label} secondary={it.description} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                {filtered.length === 0 && (
                  <ListItem><ListItemText primary="No items found" /></ListItem>
                )}
              </List>
            </Box>

            <div>
              <small style={{ color: "#5f6368" }}>Selected:</small>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                {selectedArray.length === 0 ? (
                  <Chip label="None" size="small" variant="outlined" />
                ) : (
                  selectedArray.map((id) => {
                    const userLabel = items.find((l) => +l.id === +id)?.label ?? String(id);
                    return <Chip key={id} label={userLabel} size="small" onDelete={() => toggle(id)} />;
                  })
                )}
              </Stack>
            </div>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{cancelText}</Button>
          <Button variant="contained" onClick={handleConfirm}>{confirmText}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
